import { DEFAULT_SERVICE_ITEMS } from "@/lib/service-content";

export type LocaleCode = "en" | "fr";

export type LocalizedString = Partial<Record<LocaleCode, string>>;
export type LocalizedList = Partial<Record<LocaleCode, string[]>>;

export const COLOR_PRESETS = [
  { key: "peach", label: "Pêche", value: "#FCD2C3" },
  { key: "blue", label: "Bleu", value: "#BBD8E6" },
  { key: "mint", label: "Menthe", value: "#AED8C7" },
  { key: "gold", label: "Doré", value: "#F2D5A3" },
  { key: "sand", label: "Sable", value: "#D8D1C2" },
  { key: "white", label: "Blanc", value: "#FFFFFF" },
  { key: "black", label: "Noir", value: "#111111" },
] as const;

export const FONT_SIZE_PRESETS = [
  { key: "xs", label: "XS", value: "0.85rem" },
  { key: "sm", label: "S", value: "1rem" },
  { key: "md", label: "M", value: "1.25rem" },
  { key: "lg", label: "L", value: "1.5rem" },
  { key: "xl", label: "XL", value: "2rem" },
] as const;

export type ColorPresetKey = (typeof COLOR_PRESETS)[number]["key"];
export type FontSizePresetKey = (typeof FONT_SIZE_PRESETS)[number]["key"];

export type ResponsiveFontSize = {
  base?: FontSizePresetKey;
  md?: FontSizePresetKey;
};

export type TextStyle = {
  color?: ColorPresetKey;
  size?: ResponsiveFontSize;
};

export type IconKey = string;

export type HeroSlide = {
  id: string;
  imageUrl: string;
};

export type HeroContent = {
  headline: LocalizedString;
  highlight: LocalizedString;
  ctaLabel: LocalizedString;
  ctaHref: string;
  slides: HeroSlide[];
};

export type SectionMeta = {
  iconKey?: IconKey;
  eyebrow: LocalizedString;
  title: LocalizedString;
  subtitle: LocalizedString;
  eyebrowStyle?: TextStyle;
  titleStyle?: TextStyle;
  subtitleStyle?: TextStyle;
};

export type AboutService = {
  id: string;
  iconKey: IconKey;
  position: "left" | "right";
  color: string;
  title: LocalizedString;
  description: LocalizedString;
};

export type AboutStat = {
  id: string;
  iconKey: IconKey;
  value: number;
  suffix?: string;
  label: LocalizedString;
};

export type AccordionItemContent = {
  id: string;
  iconKey?: IconKey;
  title: LocalizedString;
  imageUrl: string;
  color: string;
  paragraphs: LocalizedList;
};

export type AccordionContent = SectionMeta & {
  items: AccordionItemContent[];
};

export type BannerContent = {
  id: string;
  imageUrl: string;
  text: LocalizedString;
  colored: LocalizedString;
  ctaLabel: LocalizedString;
  ctaHref: string;
  height?: string;
};

export type SimpleSectionContent = SectionMeta & {
  ctaLabel?: LocalizedString;
  introParagraphs?: LocalizedList;
};

export type CeoWordSection = SectionMeta & {
  message: LocalizedList;
  ceoName: LocalizedString;
  ceoTitle: LocalizedString;
  ceoImageUrl: string;
  signatureImageUrl?: string;
};

export type HomepageSections = {
  hero: HeroContent;
  about: SectionMeta & {
    services: AboutService[];
    stats: AboutStat[];
  };
  ceoWord: CeoWordSection;
  services: AccordionContent;
  banners: BannerContent[];
  join: SimpleSectionContent;
  posts: SimpleSectionContent;
  partners: SectionMeta;
};

const DEFAULT_SLIDER_IMAGE = "/images/sliders/1.webp" as const;

export const DEFAULT_HOMEPAGE_SECTIONS: HomepageSections = {
  hero: {
    headline: {
      en: "Meet, share and live,",
      fr: "Rencontrez, partagez et vivez,",
    },
    highlight: {
      en: "differently",
      fr: "autrement",
    },
    ctaLabel: {
      en: "Join The Club →",
      fr: "Rejoindre le club →",
    },
    ctaHref: "/#join",
    slides: [
      {
        id: "hero-slide-0",
        imageUrl: DEFAULT_SLIDER_IMAGE,
      },
    ],
  },
  about: {
    iconKey: "Gem",
    eyebrow: {
      en: "Discover our story",
      fr: "Découvrez notre histoire",
    },
    title: {
      en: "About Uncommon 12",
      fr: "À propos d’Uncommon 12",
    },
    subtitle: {
      en: "Uncommon 12 reimagines how we gather. More than a club, it is a living scene where human connection, elegance, and meaningful experiences come together.",
      fr: "Uncommon 12 réinvente l’art de se rassembler. Plus qu’un club, c’est une scène vivante où l’humain, l’élégance et l’expérience prennent sens.",
    },
    services: [
      {
        id: "service-1",
        iconKey: "MapPin",
        position: "left",
        color: "#FCD2C3",
        title: {
          en: "A place for human encounters",
          fr: "Un lieu de rencontres humaines",
        },
        description: {
          en: "Uncommon 12 reinvents the art of bringing people together with curated experiences that blend excellence, aesthetics, and meaningful connections.",
          fr: "Uncommon 12 réinvente l’art de se rassembler. Plus qu’un lieu, c’est une scène vivante pour celles et ceux qui veulent conjuguer exigence, esthétisme et connexion humaine.",
        },
      },
      {
        id: "service-2",
        iconKey: "Layers2Icon",
        position: "right",
        color: "#BBD8E6",
        title: {
          en: "A hybrid concept",
          fr: "Un concept hybride",
        },
        description: {
          en: "The link between a like-minded club, contemporary hospitality, and an enlightened network — Geneva reimagined as intimate, inspiring, and embodied.",
          fr: "Trait d’union entre club affinitaire, hospitalité contemporaine et réseau éclairé — une autre Genève : plus intime, plus inspirante, plus incarnée.",
        },
      },
      {
        id: "service-3",
        iconKey: "SparkleIcon",
        position: "left",
        color: "#AED8C7",
        title: {
          en: "A distinct art of living",
          fr: "Un art de vivre à part",
        },
        description: {
          en: "At the crossroads of connection and place: curated encounters, rare moments, and a shared curiosity.",
          fr: "À la croisée du lien et du lieu : rencontres choisies, moments rares et curiosité partagée.",
        },
      },
      {
        id: "service-4",
        iconKey: "Hourglass",
        position: "right",
        color: "#F2D5A3",
        title: {
          en: "Meaningful shared time",
          fr: "Du sens au temps partagé",
        },
        description: {
          en: "Every exchange matters. Each instant becomes an experience — giving rhythm and intention back to time.",
          fr: "Ici, chaque échange a du sens. Chaque instant devient une expérience — on redonne du souffle au temps.",
        },
      },
    ],
    stats: [
      {
        id: "stat-1",
        iconKey: "Handshake",
        value: 100,
        suffix: "+",
        label: {
          en: "Partners",
          fr: "Partenaires",
        },
      },
      {
        id: "stat-2",
        iconKey: "Users",
        value: 150,
        suffix: "+",
        label: {
          en: "Uncommon12 members",
          fr: "Membres Uncommon12",
        },
      },
      {
        id: "stat-3",
        iconKey: "CalendarClock",
        value: 1,
        label: {
          en: "Years active",
          fr: "Année d’existence",
        },
      },
      {
        id: "stat-4",
        iconKey: "Building",
        value: 1,
        label: {
          en: "HQ",
          fr: "HQ",
        },
      },
    ],
  },
  ceoWord: {
    iconKey: "MessageSquareQuote",
    eyebrow: {
      en: "A word from our CEO",
      fr: "Le mot de notre CEO",
    },
    title: {
      en: "Crafting Uncommon 12 with intent",
      fr: "Construire Uncommon 12 avec intention",
    },
    subtitle: {
      en: "Our vision is rooted in human connection, precision, and experiences that feel both rare and welcoming.",
      fr: "Notre vision est ancrée dans le lien humain, la précision et des expériences à la fois rares et accueillantes.",
    },
    message: {
      en: [
        "Uncommon 12 was born from a desire to rethink how we gather. Every space, every encounter, and every collaboration is intentionally designed to create resonance.",
        "We partner with remarkable talents and visionaries to ensure that each moment feels curated yet effortless. Our promise is to protect this spirit as we grow.",
      ],
      fr: [
        "Uncommon 12 est né de l’envie de repenser la manière dont nous nous retrouvons. Chaque espace, chaque rencontre et chaque collaboration sont conçus avec intention pour créer de la résonance.",
        "Nous nous entourons de talents et de visionnaires pour que chaque moment soit à la fois choisi et naturel. Notre promesse est de préserver cet esprit en grandissant.",
      ],
    },
    ceoName: {
      en: "Günter Hilweg",
      fr: "Günter Hilweg",
    },
    ceoTitle: {
      en: "Founder & CEO, Uncommon 12",
      fr: "Fondateur et CEO, Uncommon 12",
    },
    ceoImageUrl: "",
    signatureImageUrl: "",
  },
  services: {
    iconKey: "Wrench",
    eyebrow: {
      en: "What we offer exclusively to our members",
      fr: "Ce que nous offrons exclusivement à nos membres",
    },
    title: {
      en: "Our Services",
      fr: "Nos Services",
    },
    subtitle: {
      en: "Uncommon 12 curates exclusive services to enrich the everyday lives of our members with refinement, connection, and usefulness.",
      fr: "Uncommon 12 propose une palette de services exclusifs, conçus pour enrichir le quotidien de ses membres avec raffinement, connexion et utilité.",
    },
    items: DEFAULT_SERVICE_ITEMS,
  },
  banners: [
    {
      id: "banner-1",
      imageUrl: DEFAULT_SLIDER_IMAGE,
      text: {
        en: "Meet, share and live,",
        fr: "Rencontrez, partagez et vivez,",
      },
      colored: {
        en: "differently",
        fr: "autrement",
      },
      ctaLabel: {
        en: "Join The Club →",
        fr: "Rejoindre le club →",
      },
      ctaHref: "/#join",
      height: "h-70",
    },
    {
      id: "banner-2",
      imageUrl: DEFAULT_SLIDER_IMAGE,
      text: {
        en: "Meet, share and live,",
        fr: "Rencontrez, partagez et vivez,",
      },
      colored: {
        en: "differently",
        fr: "autrement",
      },
      ctaLabel: {
        en: "Join The Club →",
        fr: "Rejoindre le club →",
      },
      ctaHref: "/#join",
      height: "h-70",
    },
    {
      id: "banner-3",
      imageUrl: DEFAULT_SLIDER_IMAGE,
      text: {
        en: "Meet, share and live,",
        fr: "Rencontrez, partagez et vivez,",
      },
      colored: {
        en: "differently",
        fr: "autrement",
      },
      ctaLabel: {
        en: "Join The Club →",
        fr: "Rejoindre le club →",
      },
      ctaHref: "/#join",
      height: "h-70",
    },
    {
      id: "banner-4",
      imageUrl: DEFAULT_SLIDER_IMAGE,
      text: {
        en: "Meet, share and live,",
        fr: "Rencontrez, partagez et vivez,",
      },
      colored: {
        en: "differently",
        fr: "autrement",
      },
      ctaLabel: {
        en: "Join The Club →",
        fr: "Rejoindre le club →",
      },
      ctaHref: "/#join",
      height: "h-70",
    },
  ],
  join: {
    iconKey: "Lock",
    eyebrow: {
      en: "Don’t wait any longer to",
      fr: "N'attendez plus pour",
    },
    title: {
      en: "Join Uncommon 12",
      fr: "Rejoindre Uncommon 12",
    },
    subtitle: {
      en: "Uncommon 12 is not a closed club — it is a confidential circle built on chosen affinities.",
      fr: "Uncommon 12 n’est pas un club fermé — c’est un cercle confidentiel, basé sur les affinités choisies.",
    },
    ctaLabel: {
      en: "Join The Club →",
      fr: "Rejoindre le club →",
    },
    introParagraphs: {
      fr: [
        "Pour préserver l’esprit du Club, l’accès se fait par parrainage. Entrez le code d’invitation que vous avez reçu pour accéder au formulaire d’adhésion.",
        "Vous n’avez pas encore de parrain ou marraine ?",
        "Remplissez un court formulaire pour demander un entretien d’exploration avec Günter, ou laissez-nous organiser le premier échange selon votre préférence.",
        "L’échange peut avoir lieu en présentiel ou par téléphone, dans un esprit chaleureux, simple et ouvert.",
      ],
      en: [
        "To preserve the Club’s spirit, admission works through sponsorship. Enter the invitation code you received to access the membership form.",
        "No sponsor yet?",
        "Fill out a short form to request an exploratory conversation with Günter, or let us arrange the first exchange according to your preference.",
        "The exchange can take place in person or by phone, in a warm, simple, and open spirit.",
      ],
    },
  },
  posts: {
    iconKey: "NewspaperIcon",
    eyebrow: {
      en: "Not to be missed",
      fr: "à ne pas manquer",
    },
    title: {
      en: "Our News",
      fr: "Notre actualité",
    },
    subtitle: {
      en: "Follow the latest announcements, events, and behind-the-scenes moments of Uncommon 12 — a privileged glimpse into our world in motion.",
      fr: "Suivez les dernières annonces, événements, et coulisses du cercle Uncommon 12 — un aperçu privilégié de notre univers en mouvement.",
    },
    ctaLabel: {
      en: "Read all posts →",
      fr: "Lire toutes les actualités →",
    },
  },
  partners: {
    iconKey: "Handshake",
    eyebrow: {
      en: "They believe in us",
      fr: "Ils nous soutiennent",
    },
    title: {
      en: "Our partners",
      fr: "Nos partenaires",
    },
    subtitle: {
      en: "They support our vision and help us craft the Uncommon 12 experience.",
      fr: "Ils soutiennent notre vision et nous accompagnent dans l’expérience Uncommon 12.",
    },
  },
};
